/**
 * The RouteOnMap view allows to display a route on map.
 *
 * The tomtom.routeOnMap() method creates a new instance of RouteOnMap view.
 *
 * @module Widgets
 * @class routeOnMap
 *
 */
define('widgets/views/routeOnMap',['display/map', 'services/routing', 'services/reverseGeocode/reverseGeocode',
    'services/converters', 'services/utils'],
    function(L, routingService, reverseGeocodeService, converters, utils) {
        'use strict';

        var EVENTS = {
            MarkerDragEnd: 'markerDragEnd',
            Draw: 'draw',
            RouteChanged: 'routeChanged'
        };

        var RouteOnMap;

        function createMarkerIcon(type) {
            var generalMarkerIcon = {
                background: {
                    iconSize: L.Browser.retina ? [34, 34] : [20, 20],
                    iconAnchor: L.Browser.retina ? [17, 17] : [10, 10],
                    icon: 'icon-shield'
                },
                icon: {
                    iconSize: L.Browser.retina ? [30, 30] : [18, 18],
                    iconAnchor: L.Browser.retina ? [-2, -2] : [-1, -1],
                    icon: 'icon-flag-white'
                }
            };
            if (type === 'start') {
                generalMarkerIcon.icon.icon = 'icon-start-white';
            } else if (type === 'end') {
                generalMarkerIcon.icon.icon = 'icon-end-white';
            }
            return generalMarkerIcon;
        }

        function getRouteStyle(style) {
            var styleIndex = 0;
            return function() {
                styleIndex += 1;
                return style[styleIndex % style.length] || style;
            };
        }

        function convertPoints(points) {
            var point,
                i;
            for (i = 0; i < points.length; i++) {
                point = points[i];
                points[i] = point ? L.latLng(converters.point(point).split(',')) : undefined;
            }
            return points;
        }

        function getIconOptions(points, index, options) {
            var iconOptions = utils.clone(options.generalMarker);
            if (index === 0) {
                utils.addFields(options.startMarker, iconOptions);
                iconOptions.icon = options.startMarker.icon;
            } else if (index === points.length - 1) {
                utils.addFields(options.endMarker, iconOptions);
                iconOptions.icon = options.endMarker.icon;
            } else {
                utils.addFields(options.waypointMarker, iconOptions);
                iconOptions.icon = options.waypointMarker.icon;
            }
            if (options.generalMarker.zIndexOffset) {
                iconOptions.zIndexOffset = options.generalMarker.zIndexOffset;
            }
            return iconOptions;
        }

        function onDragListener(points, e, markers, callback) {
            var index = markers.indexOf(e.target);
            points[index] = e.target.getLatLng();
            callback.call(this, points, index);
        }

        function getAllFilledLocations(points) {
            var filledLocations = [],
                i;
            for (i = 0; i < points.length; i++) {
                if (points[i] !== undefined) {
                    filledLocations.push(points[i]);
                }
            }
            return filledLocations;
        }

        RouteOnMap = L.LayerGroup.extend({
            includes: L.Mixin.Events,

            options: {
                generalMarker: {icon: L.svgIcon(createMarkerIcon()), draggable: false},
                startMarker: {icon: L.svgIcon(createMarkerIcon('start')), draggable: false},
                waypointMarker: {icon: L.svgIcon(createMarkerIcon('waypoint')), draggable: false},
                endMarker: {icon: L.svgIcon(createMarkerIcon('end')), draggable: false},
                position: 'topleft',
                routeStyle: {color: '#00d7ff', opacity: 0.8},
                alternativeStyles: [
                    {color: '#ff00d2', opacity: 0.8},
                    {color: '#b922ff', opacity: 0.8}
                ],
                routingDelay: 1000,
                serviceOptions: {}
            },

            initialize: function(options) {
                L.Util.setOptions(this, options);
            },

            /**
             * Method used to create markers on a map, basing on given points.
             *
             * @method createMarkers
             * @param {Array} points List of points to create markers onto.
             */
            createMarkers: function(points) {
                var that = this,
                    markers = [],
                    iconOptions,
                    marker,
                    point,
                    i;

                for (i = 0; i < points.length; i++) {
                    point = points[i];

                    if (point !== undefined) {
                        iconOptions = getIconOptions(points, i, this.options);
                        marker = L.marker(point, iconOptions);
                        markers.push(marker);

                        if (iconOptions.draggable) {
                            marker.on('dragend', function(event) {
                                onDragListener.call(that, points, event, markers, that.onDragEnd);
                            });
                            marker.on('drag', function(event) {
                                onDragListener.call(that, points, event, markers, that.onDrag);
                            });
                        }
                    }
                }

                delete this.markers;
                if (markers.length > 0) {
                    this.markers = new L.featureGroup(markers);
                    this.markers.addTo(this._map);
                }
            },

            /**
             * Method used to create a route on a map between given points.
             * This method executes TomTom Routing Service to get a route.
             * If route is found, this method draws it on a map. If not - shows proper notification.
             *
             * @method createRoute
             * @param {Array} points List of points to create a route between.
             */
            createRoute: function(points) {
                var that = this;
                this.options.serviceOptions.locations = points;
                routingService(this.options.serviceOptions).go().then(function(routeGeoJson) {
                    if (routeGeoJson.features.length > 0) {
                        that.drawRouteWithGeoJson(routeGeoJson);
                    } else {
                        that.clearRoute();
                        L.messageBox({closeAfter: 1500}).openOn(that._map)
                            .setContent('Could not find a route. <br/>You might trying to route between continents.');
                    }
                });
            },

            /**
             * Method fired when user drags a marker. Allows to look for routes while dragging a marker and within delay
             * equal to the value passed in options [options.routingDelay].
             *
             * @method onDrag
             * @param {Number} points List of points to create a route within.
             */
            onDrag: function(points) {
                var that = this;
                if (this.recalculateRoute === true) {
                    setTimeout(function() {
                        that.clearRoute();
                        that.createRoute(points);
                        that.recalculateRoute = true;
                    }, this.options.routingDelay);
                    this.recalculateRoute = false;
                }
            },

            /**
             * Method fired when user drops a marker (equal to dragEnd event). Redraws route according to given points
             * and executes TomTom Reverse Geocode Service to get human-readable location when marker was dropped. That
             * fires <a href="#events_MarkerDragEnd">Event.MarkerDragEnd</a> event with service's call result and index
             * of a marker.
             *
             * @method onDragEnd
             * @param {Array} points List of points.
             * @param {Number} index Index of the marker that was dropped.
             */
            onDragEnd: function(points, index) {
                var that = this;
                this.clearRoute();
                this.createRoute(points);
                this.once(EVENTS.RouteChanged, this.fitMapBoundsToRoute, this);

                reverseGeocodeService().position(points[index]).go().then(function(result) {
                    that.fire(EVENTS.MarkerDragEnd, {object: result, markerIndex: index});
                });
            },

            /**
             * Method that removes all markers.
             *
             * @method clearMarkers
             */
            clearMarkers: function() {
                if (this.markers) {
                    this.markers.clearLayers();
                }
                this.markers = undefined;
            },

            /**
             * Method that clears shown route.
             *
             * @method clearMarkers
             */
            clearRoute: function() {
                if (this.routeOnMapLayer) {
                    this._map.removeLayer(this.routeOnMapLayer);
                }
                this.routeOnMapLayer = undefined;
            },

            /**
             * Method that fits map to the drawn route.
             *
             * @method fitMapBoundsToRoute
             */
            fitMapBoundsToRoute: function() {
                var areaToBoundTo = this.routeOnMapLayer || this.markers;
                if (areaToBoundTo) {
                    this._map.fitBounds(areaToBoundTo.getBounds(), {padding: [5, 5]});
                }
            },

            /**
             * Method that draws a route basing on a passed geoJson. Fires
             * <a href="#events_RouteChanged">Event.RouteChanged</a> event.
             *
             * @method drawRouteWithGeoJson
             * @param {Object} geoJson Represents geoJson data of a route.
             */
            drawRouteWithGeoJson: function(geoJson) {
                this.clearRoute();
                this.routeOnMapLayer = L.geoJson(geoJson, {
                    onEachFeature: this.options.onEachFeature,
                    style: getRouteStyle(this.options.serviceOptions.maxAlternatives > 0 ?
                        this.options.alternativeStyles.concat([this.options.routeStyle]) : this.options.routeStyle)
                });
                this._map.addLayer(this.routeOnMapLayer);
                this.fire(EVENTS.RouteChanged, {
                    object: geoJson.features[0].properties.summary,
                    instructions: geoJson.features[0].properties.guidance
                });
            },

            /**
             * Method that takes care of redrawing markers and a route.
             *
             * @method drawRoute
             * @param {Array} points List of points to draw a route between.
             */
            drawRoute: function(points) {
                this.clear();
                this.recalculateRoute = true;
                this.createMarkers(points);

                if (points.length >= 2) {
                    this.createRoute(points);
                }
            },

            /**
             * The method to draw a route between two or more points.
             *
             * @method draw
             * @param {Array} points Array of geographical locations that between route will be drawn. There are
             *     multiple formats supported (see more at e.g. _Search_ page in _center_ method). This method also
             *     fires <a href="#events_Draw">Event.Draw</a> event that announces that route is drawn.
             * @example
             * ```javascript
             *
             * var startPoint = {lat: 49.29917, lon: 19.94902};
             * var waypoint = {lat: 42.25917, lon: 19.98902};
             * var endPoint = {lat: 52.23498, lon: 21.00849};
             * var points = [startPoint, waypoint, endPoint];
             * var routeOnMapView = routeOnMap().addTo(map);
             * routeOnMapView.draw(points);
             *
             * ```
             */
            draw: function(points) {
                points = convertPoints(points);
                points = getAllFilledLocations(points);
                this.drawRoute(points);
                this.fitMapBoundsToRoute();
                this.once(EVENTS.RouteChanged, this.fitMapBoundsToRoute, this);
                this.fire(EVENTS.Draw, {});
            },

            /**
             * The method to clear a route and markers.
             *
             * @method clear
             *
             * @example
             * ```javascript
             *
             * var routeOnMapView = routeOnMap(optionsForRouteOnMapView).addTo(map);
             * routeOnMapView.clear();
             * ```
             */
            clear: function() {
                this.clearMarkers();
                this.clearRoute();
            },

            /**
             * Event that is fired after dropping the marker. Contains information about marker's position.
             *
             * @event MarkerDragEnd
             * @param {Object} object Information of dropped marker's position (got from reverse geocode service).
             * @param {Number} markerIndex Index of the marker on a map.
             * @param {String} type Event's name, equal to "markerDragEnd".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var routeOnMapView = routeOnMap().addTo(map);
             * routeOnMapView.on(routeOnMapView.Event.MarkerDragEnd, function (eventObject) {
             *      console.log(eventObject);
             * });
             * ```
             */
            /**
             * Event that is fired after drawing a route on a map.
             *
             * @event Draw
             * @param {String} type Event's name, equal to "draw".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var routeOnMapView = routeOnMap().addTo(map);
             * routeOnMapView.on(routeOnMapView.Event.Draw, function (eventObject) {
             *      console.log(eventObject);
             * });
             * ```
             */
            /**
             * Event that is fired when route was changed.
             *
             * @event RouteChanged
             * @param {Object} object Route summary that contains arrival time, departure time, route length, traffic
             *      and travel time.
             * @param {String} type Event's name, equal to "routeChanged".
             * @param {Element} target Event's target.
             *
             * @example
             * ```javascript
             *
             * var routeOnMapView = routeOnMap().addTo(map);
             * routeOnMapView.on(routeOnMapView.Event.RouteChanged, function (eventObject) {
             *      console.log(eventObject);
             * });
             * ```
             */
            Event: EVENTS
        });

        /**
         * The method to instantiates the view. The options can be passed on creation.
         *
         * @method routeOnMap
         * @param {Object} [options] Options to be passed to the routing view.
         * @param {Object} [options.markerGeneral] General marker options that applies on all markers.
         * @param {Object} [options.markerGeneral.icon] Default icon for every marker. Icon should extend L.icon.
         * @param {Boolean} [options.markerGeneral.draggable=false] Determines default drag behaviour for every marker.
         * @param {Object} [options.startMarker] Marker options for starting marker.
         * @param {Object} [options.startMarker.icon] Icon for start marker. This field should extend L.icon.
         * @param {Boolean} [options.startMarker.draggable] Determines whether start marker should be draggable.
         * @param {Object} [options.waypointMarker] Marker options for markers between start and end points.
         * @param {Object} [options.waypointMarker.icon] Icon for markers between start and end points. This field
         *                  should extend L.icon.
         * @param {Boolean} [options.waypointMarker.draggable] Determines whether start marker should be draggable.
         * @param {Object} [options.endMarker] Marker options for end marker.
         * @param {Object} [options.endMarker.icon] Icon for end marker. This field should extend L.icon.
         * @param {Boolean} [options.endMarker.draggable] Determines whether end marker should be draggable.
         * @param {Object} [options.routeStyle] A style object for the polyline to be used.
         * @param {Array} [options.alternativeStyles] If option to display alternatives is passed to the service
         *     options then there is a need to pass array of style's options for each alternative route.
         * @param {Integer} [options.routingDelay] Determines the time that within routing service will be called. Used
         *                  when user is dragging a marker.
         * @param {Function} [options.onEachFeature] A function that will be called for each feature, it takes feature
         *                  and layer as the attributes. Can be used in example to bind popup to the route.
         * @param {Object} [options.serviceOptions] Options to be passed to the routing service. For details check
         *     routingService documentation.
         *
         * @example
         * ```javascript
         *
         * var optionsForRouteOnMapView = {
         *       generalMarker: {
         *          draggable: true
         *       },
         *       startMarker: {
         *          background: {
         *              iconSize: L.Browser.retina ? [50, 50] : [30, 36],
         *              iconAnchor: L.Browser.retina ? [25, 50] : [15, 36],
         *              icon: 'marker'
         *          },
         *          icon: {
         *              iconSize: L.Browser.retina ? [50, 50] : [30, 30],
         *              iconAnchor: [0, 0],
         *              icon: 'flag-white'
         *          },
         *       },
         *       waypointMarker: {
         *          L.icon({
         *              iconUrl: 'waypoint.png',
         *              iconSize: L.Browser.retina ? [50, 50] : [30, 30],
         *              iconAnchor: L.Browser.retina ? [25, 25] : [15, 15]
         *          })
         *       },
         *       endMarker: {
         *          L.icon({
         *              iconUrl: 'end.png',
         *              iconSize: L.Browser.retina ? [50, 50] : [30, 30],
         *              iconAnchor: L.Browser.retina ? [25, 25] : [15, 15]
         *          })
         *       },
         *       routeStyle: {color: "#00d7ff", opacity: 0.8}
         *   };
         *
         * var routeOnMapView = routeOnMap(optionsForRouteOnMapView).addTo(map);
         * ```
         */
        return function(options) {
            return new RouteOnMap(options);
        };
    });

